// Offer Ladder persistence + the pull from the Accelerator's locked My Offer.
//
// This is the seam that makes the Offer Ladder a real, multi-instance tool.
// Today it persists to the browser (localStorage) so the full add → list →
// edit → delete loop works on dev with no backend. When we're ready, swap the
// storage functions below to Supabase calls (the `offers` table, created in
// supabase/0001_momentum_schema.sql) and every caller keeps working unchanged —
// the shapes stay the same.
//
// The *reading* of Brill's locked prose lives in `offerParse.js` and is pure and
// synchronous. Only this file touches storage, so only this file goes async when
// the swap happens. Everything from offerParse is re-exported here, so existing
// imports of `offerStore` keep working either way.

import {
  SOURCE_SECTION,
  DEFAULT_CORE_NAME,
  newOfferId,
  readSignatureOffer,
} from "./offerParse";

export {
  SOURCE_SECTION,
  EMPTY_PRICING,
  DEFAULT_CORE_NAME,
  newOfferId,
  readPricing,
  formatPricing,
  hasSignatureOffer,
  readSignatureOffer,
  sourceChanged,
} from "./offerParse";

const KEY = "brill_offers_v1";
// Separate from the offers themselves so deleting the seeded offer doesn't make
// it reappear on the next load. The pull happens once, not every mount.
const SEED_KEY = "brill_offers_seeded_v1";

export function loadOffers() {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveAll(list) {
  try { localStorage.setItem(KEY, JSON.stringify(list)); } catch { /* ignore quota errors */ }
}

// Insert new offers at the top; update in place if the id already exists.
export function upsertOffer(offer) {
  const list = loadOffers();
  const i = list.findIndex((o) => o.id === offer.id);
  const stamped = { ...offer, updatedAt: Date.now() };
  if (i >= 0) list[i] = stamped;
  else list.unshift({ ...stamped, createdAt: Date.now() });
  saveAll(list);
  return loadOffers();
}

export function deleteOffer(id) {
  saveAll(loadOffers().filter((o) => o.id !== id));
  return loadOffers();
}

/* ── The pull ─────────────────────────────────────────────────────────────────
   The signature offer is the single offer type we can infer with confidence — it
   comes from the section named for it — so this is the only place a type is
   assigned without her choosing it.

   Two ways in, one behaviour: `seedFromAccelerator` runs automatically on her
   first arrival, `pullSignatureOffer` is the same pull asked for by hand. The
   automatic one is once-ever so a deleted row never resurrects itself; the
   explicit one is what makes the pull reachable again afterwards.

   Provenance is a snapshot, not a mirror: `sourceText` / `sourceHash` record
   what was pulled so she can edit freely afterwards and still be told when the
   source has moved. Nothing here ever overwrites an edit; that only happens
   through `resyncOffer`, which she triggers.                                   */
export function hasSeeded() {
  try { return localStorage.getItem(SEED_KEY) === "1"; } catch { return true; }
}

function markSeeded() {
  try { localStorage.setItem(SEED_KEY, "1"); } catch { /* ignore */ }
}

// The one place the pulled record's shape is defined, so the automatic and the
// manual pull can never drift apart.
function pull(offerOutputs) {
  const read = readSignatureOffer(offerOutputs);
  if (!read) return null;
  markSeeded();
  return upsertOffer({
    id: newOfferId(),
    type: "core",
    name: read.name || DEFAULT_CORE_NAME,
    summary: read.summary,
    pricing: read.pricing,
    tiedTo: "",
    status: "draft",
    source: "accelerator",
    sourceSection: SOURCE_SECTION,
    sourceText: read.sourceText,
    sourceHash: read.sourceHash,
  });
}

/**
 * Pull the locked signature offer into the ladder if it hasn't been pulled
 * before. Returns the offer list either way, so callers can use it directly.
 */
export function seedFromAccelerator(offerOutputs = {}) {
  if (hasSeeded()) return loadOffers();
  // No locked offer yet — `pull` returns null without marking seeded, so this
  // still happens on a later visit once she's locked it.
  return pull(offerOutputs) || loadOffers();
}

/**
 * The same pull, asked for rather than automatic — how she gets the row back
 * after deleting it, and the only route in once the once-ever flag is set.
 * Callers must check `hasSignatureOffer` first, and must not offer this when a
 * pulled row already exists; that case belongs to `resyncOffer`.
 */
export function pullSignatureOffer(offerOutputs = {}) {
  return pull(offerOutputs) || loadOffers();
}

/**
 * Replace a pulled offer's fields with what's in My Offer now. Destructive by
 * design — the caller must confirm first. Her own additions that the pull can't
 * produce (type, tied-to, status) are kept, since re-reading the source says
 * nothing about them.
 */
export function resyncOffer(offer, offerOutputs = {}) {
  const read = readSignatureOffer(offerOutputs);
  if (!read) return loadOffers();
  return upsertOffer({
    ...offer,
    name: read.name || offer.name,
    summary: read.summary,
    pricing: read.pricing,
    sourceText: read.sourceText,
    sourceHash: read.sourceHash,
  });
}
